import { ThumbsDown, ThumbsUp } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

export type SummaryVote = 'up' | 'down';

/** The thumbs draw at 26px: a 14px icon inside 6px of padding. */
const THUMB_HIT_SLOP = getAccessibleHitSlop(26);

const THUMB_ICONS = { up: ThumbsUp, down: ThumbsDown } as const;

/**
 * "How did we do?" and a thumb each way, the one feedback control every generated summary shares: the
 * Inquiry Library post summary and the case summary on an inquiry. Each surface owns the vote,
 * what it reports and what follows a vote; this only draws the row, so the two cannot drift.
 *
 * The selected thumb sits on a tinted chip, because a shade of color alone read as unselected on
 * a phone. Pressing it again is the caller's cue to take the vote back.
 */
export function SummaryThumbs({
  vote,
  onRate,
  labels,
  textClassName,
  showPrompt = true,
}: {
  vote: SummaryVote | null | undefined;
  onRate: (pressed: SummaryVote) => void;
  /** What each thumb says to a screen reader, naming the summary it judges. */
  labels: Record<SummaryVote, string>;
  /** The surface's footer type size, so the prompt matches the text beside it. */
  textClassName?: string;
  /** False draws the thumbs alone, for a row that already says what they rate. */
  showPrompt?: boolean;
}) {
  return (
    <HStack className="items-center gap-1.5">
      {showPrompt ? (
        <Text size="xs" className={twJoin('text-typography-700', textClassName)}>
          How did we do?
        </Text>
      ) : null}

      {(['up', 'down'] as const).map((thumb) => (
        <Pressable
          key={thumb}
          onPress={() => onRate(thumb)}
          hitSlop={THUMB_HIT_SLOP}
          accessibilityRole="button"
          accessibilityState={{ selected: vote === thumb }}
          accessibilityLabel={labels[thumb]}
          className={twJoin(
            'rounded-full p-1.5 web:cursor-pointer',
            vote === thumb ? 'bg-secondary-100' : 'web:hover:bg-background-100'
          )}>
          <Icon
            as={THUMB_ICONS[thumb]}
            size="xs"
            className={vote === thumb ? 'text-secondary-700' : 'text-typography-700'}
          />
        </Pressable>
      ))}
    </HStack>
  );
}
